import { Link } from 'react-router-dom';
import { useReveal } from '../lib/anim/useReveal';

type Tier = {
  slug: 'pilot' | 'build' | 'studio';
  name: string;
  price: string;
  priceSub?: string;
  tracks: string;
  products: string;
  capacity?: string;
  extras?: string[];
  targetBuyer: string;
  featured?: boolean;
};

const TIERS: Tier[] = [
  {
    slug: 'pilot',
    name: 'Clarity Pilot',
    price: '$149',
    tracks: 'Track A only',
    products: '1 product',
    targetBuyer: 'First-time platform user validating a vertical idea against a known engine.',
  },
  {
    slug: 'build',
    name: 'Clarity Build',
    price: '$499',
    tracks: 'Tracks A + B',
    products: '1 product',
    capacity: '15 hrs/mo AI-team Slack capacity',
    targetBuyer: 'Founder with a custom idea or who has outgrown Pilot.',
    featured: true,
  },
  {
    slug: 'studio',
    name: 'Clarity Studio',
    price: '$1,499',
    priceSub: '+ 5% rev share above $10k customer MRR',
    tracks: 'Tracks A + B',
    products: '3–5 products',
    capacity: 'Fair-use Slack capacity',
    extras: ['1 monthly Ryan strategy call'],
    targetBuyer: 'High-growth operator running multiple plays.',
  },
];

function TierCard({ tier, delay }: { tier: Tier; delay: 0 | 1 | 2 | 3 | 4 | 5 }) {
  const reveal = useReveal({ delay });

  const cardStyle: React.CSSProperties = tier.featured
    ? {
        background: 'var(--color-surface)',
        border: '1px solid var(--color-hairline)',
        borderRadius: '8px',
        position: 'relative',
        overflow: 'hidden',
      }
    : {
        background: 'var(--color-surface)',
        border: '1px solid var(--color-hairline)',
        borderRadius: '8px',
        position: 'relative',
      };

  return (
    <div
      ref={reveal.ref}
      {...reveal.props}
      className={`pricing-card${tier.featured ? ' pricing-card-featured' : ''} p-8 flex flex-col gap-5${reveal.props.className ? ' ' + reveal.props.className : ''}`}
      style={cardStyle}
    >
      {/* 4px indigo top stripe for featured */}
      {tier.featured && (
        <span
          aria-hidden
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: 'var(--color-accent)',
            display: 'block',
          }}
        />
      )}

      {/* Most popular pill — accent-soft */}
      {tier.featured && (
        <span
          className="absolute top-6 right-5 text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wide"
          style={{
            background: 'var(--color-accent-soft)',
            color: 'var(--color-accent)',
            fontFamily: 'var(--font-mono)',
            border: '1px solid rgba(79, 70, 229, 0.2)',
          }}
        >
          Most popular
        </span>
      )}

      <div>
        <p
          className="text-xs uppercase tracking-[0.12em] font-medium mb-2"
          style={{ color: 'var(--color-mute)', fontFamily: 'var(--font-mono)' }}
        >
          {tier.name}
        </p>
        <div className="flex items-baseline gap-1">
          {/* Price in JetBrains Mono ink — NO gold */}
          <span
            className="text-4xl font-medium"
            style={{ fontFamily: 'var(--font-mono)', color: 'var(--color-ink)' }}
          >
            {tier.price}
          </span>
          <span
            className="text-sm"
            style={{ color: 'var(--color-mute)', fontFamily: 'var(--font-body)' }}
          >
            /mo
          </span>
        </div>
        {tier.priceSub && (
          <p
            className="mt-1 text-xs"
            style={{ color: 'var(--color-mute)', fontFamily: 'var(--font-body)' }}
          >
            {tier.priceSub}
          </p>
        )}
      </div>

      {/* Feature list */}
      <ul className="flex-1 space-y-2 text-sm" style={{ color: 'var(--color-ink)', fontFamily: 'var(--font-body)' }}>
        <li className="flex gap-2 items-start">
          <span style={{ color: 'var(--color-accent)' }} className="mt-0.5 shrink-0">✓</span>
          {tier.tracks}
        </li>
        <li className="flex gap-2 items-start">
          <span style={{ color: 'var(--color-accent)' }} className="mt-0.5 shrink-0">✓</span>
          {tier.products}
        </li>
        {tier.capacity && (
          <li className="flex gap-2 items-start">
            <span style={{ color: 'var(--color-accent)' }} className="mt-0.5 shrink-0">✓</span>
            {tier.capacity}
          </li>
        )}
        {tier.extras?.map((extra) => (
          <li key={extra} className="flex gap-2 items-start">
            <span style={{ color: 'var(--color-accent)' }} className="mt-0.5 shrink-0">✓</span>
            {extra}
          </li>
        ))}
      </ul>

      {/* Target buyer */}
      <p
        className="text-xs leading-relaxed pt-4"
        style={{
          color: 'var(--color-mute)',
          borderTop: '1px solid var(--color-hairline)',
          fontFamily: 'var(--font-body)',
        }}
      >
        {tier.targetBuyer}
      </p>

      {/* CTA */}
      <Link
        to={`/signup?tier=${tier.slug}`}
        className={`${tier.featured ? 'btn-primary' : 'btn-ghost'} w-full justify-center text-sm`}
      >
        Get started →
      </Link>
    </div>
  );
}

export function PricingTeaser() {
  const heading = useReveal();

  return (
    <section className="py-24" style={{ background: 'var(--color-bg)' }}>
      <div className="max-w-6xl mx-auto px-6">
        {/* Section heading */}
        <div
          ref={heading.ref}
          {...heading.props}
          className={`mb-14 text-center${heading.props.className ? ' ' + heading.props.className : ''}`}
        >
          <p
            className="text-xs uppercase tracking-[0.12em] mb-3 font-medium"
            style={{ color: 'var(--color-mute)', fontFamily: 'var(--font-mono)' }}
          >
            Pricing
          </p>
          <h2
            className="text-3xl md:text-4xl font-bold"
            style={{ fontFamily: 'var(--font-display)', color: 'var(--color-ink)' }}
          >
            Rent the apparatus. Keep the upside.
          </h2>
          <p
            className="mt-4 max-w-xl mx-auto text-base"
            style={{ color: 'var(--color-mute)', fontFamily: 'var(--font-body)' }}
          >
            No equity. No lock-in. Pause or cancel any month.
          </p>
        </div>

        {/* Tier cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
          {TIERS.map((tier, i) => (
            <TierCard key={tier.slug} tier={tier} delay={(i as 0 | 1 | 2)} />
          ))}
        </div>

        <p
          className="mt-8 text-center text-xs"
          style={{ color: 'var(--color-mute)', fontFamily: 'var(--font-body)' }}
        >
          All prices USD. Rev share applies to Studio only above $10k customer MRR.
        </p>
      </div>
    </section>
  );
}
